// Query string contract (see .nobears/plan/…/45-api-contract.md): the only
// place that knows param names; an absent param means "no restriction".

import { HOUSING_TYPE_SLUGS } from './filterConfig';

export const DEFAULT_RADIUS = 25;

// Hardcoded status filter values (WHN-2099). The built web API accepts the raw
// ConstructionNumberStatus values, not slugs - one vocabulary, and the page
// querystring is one-to-one the API call (docs web-api-contract.md, WHN-2117).
// These options carry labelKey rather than a label: this module is evaluated on
// import, before the mount payload reaches setTranslations(), so t() has to run
// in the consuming component.
export const STATUS_OPTIONS = [
  { slug: 'Beschikbaar', labelKey: 'listing-filter-bar.status.available', color: '#acc33e' },
  { slug: 'In optie', labelKey: 'listing-filter-bar.status.option', color: '#f98100' }
];

export const DEFAULT_SORT = 'price-asc';

// Hardcoded sort options (WHN-2099).
export const SORT_OPTIONS = [
  // Without long/lat the API has nothing to measure from and falls back to
  // price ascending (WHN-2167).
  { slug: 'distance-asc', labelKey: 'listing-filter-bar.sort.distance-asc' },
  { slug: 'name-asc', labelKey: 'listing-filter-bar.sort.name-asc' },
  { slug: 'name-desc', labelKey: 'listing-filter-bar.sort.name-desc' },
  { slug: 'price-asc', labelKey: 'listing-filter-bar.sort.price-asc' },
  { slug: 'price-desc', labelKey: 'listing-filter-bar.sort.price-desc' },
  { slug: 'm2-asc', labelKey: 'listing-filter-bar.sort.m2-asc' },
  { slug: 'm2-desc', labelKey: 'listing-filter-bar.sort.m2-desc' },
  { slug: 'status', labelKey: 'listing-filter-bar.sort.status' }
];

// The bedroom buttons, and which of them means "or more" (WHN-2148: three
// options, so 3 covers 3, 4 and 5). One constant because the buttons, the
// search bar summary and the API parameter all have to name the same top
// option, and the API reads any `N+` as a minimum.
export const BEDROOM_OPTIONS = [1, 2, 3];
export const BEDROOM_PLUS_FROM = BEDROOM_OPTIONS[BEDROOM_OPTIONS.length - 1];

export const criteriaDefaults = () => ({
  loc: '',
  long: null,
  lat: null,
  radius: DEFAULT_RADIUS,
  pmin: '',
  pmax: '',
  m2: null,
  perceel: null,
  beds: [],
  types: [],
  statuses: [],
  sort: DEFAULT_SORT
});

const RANGE_PARAMS = { m2: ['m2Min', 'm2Max'], perceel: ['perceelMin', 'perceelMax'] };
const CSV_PARAMS = ['beds', 'types', 'statuses'];
const CRITERIA_PARAMS = [
  'loc',
  'long',
  'lat',
  'radius',
  'pmin',
  'pmax',
  'm2Min',
  'm2Max',
  'perceelMin',
  'perceelMax',
  'sort',
  ...CSV_PARAMS
];

// The criteria the filter bar shows as pills. "Filters wissen" there owns only
// these; the search bar's location, budget and housing segments each have their
// own clear button and must survive it (WHN-2127). The sheet's "Alle filters
// wissen" still resets everything through hasActiveCriteria/reset.
export const FILTER_BAR_KEYS = ['types', 'statuses', 'sort'];

const isDefaultValue = (criteria, key, defaults) =>
  Array.isArray(defaults[key]) ? criteria[key].length === 0 : criteria[key] === defaults[key];

export const hasActiveFilterBarCriteria = (criteria) => {
  const defaults = criteriaDefaults();
  return FILTER_BAR_KEYS.some((key) => !isDefaultValue(criteria, key, defaults));
};

export const filterBarDefaults = () => {
  const defaults = criteriaDefaults();
  return Object.fromEntries(FILTER_BAR_KEYS.map((key) => [key, defaults[key]]));
};

export const toQueryParams = (criteria, rangeBounds = {}) => {
  const params = new URLSearchParams();

  // loc is only a filter together with its coordinates: a label without long/lat
  // filters nothing, so it is never persisted or shared as if it were. The only
  // place loc-without-coordinates lives is a live, in-progress typed query.
  if (criteria.loc && criteria.long !== null && criteria.lat !== null) {
    params.set('loc', criteria.loc);
    params.set('long', String(criteria.long));
    params.set('lat', String(criteria.lat));
    if (criteria.radius !== DEFAULT_RADIUS) {
      params.set('radius', criteria.radius === null ? '' : String(criteria.radius));
    }
  }
  if (criteria.pmin) params.set('pmin', String(criteria.pmin));
  if (criteria.pmax) params.set('pmax', String(criteria.pmax));

  // a slider handle on its bound is "no restriction": that side is omitted,
  // so 200+ sends no max param at all
  Object.entries(RANGE_PARAMS).forEach(([key, [minParam, maxParam]]) => {
    const range = criteria[key];
    if (!range) return;
    const bounds = rangeBounds[key] || [0, Infinity];
    if (range[0] !== null && range[0] > bounds[0]) params.set(minParam, String(range[0]));
    if (range[1] !== null && range[1] < bounds[1]) params.set(maxParam, String(range[1]));
  });

  CSV_PARAMS.forEach((key) => {
    if (criteria[key].length) params.set(key, criteria[key].join(','));
  });

  if (criteria.sort !== DEFAULT_SORT) params.set('sort', criteria.sort);

  return params;
};

export const fromQueryParams = (searchParams) => {
  const criteria = criteriaDefaults();

  const long = Number.parseFloat(searchParams.get('long'));
  const lat = Number.parseFloat(searchParams.get('lat'));
  const hasCoordinates =
    Number.isFinite(long) && Number.isFinite(lat) && Math.abs(long) <= 180 && Math.abs(lat) <= 90;

  // loc is ignored without coordinates: it only counts as a filter alongside
  // long/lat (see toQueryParams), so a bare ?loc=... is not shown as active.
  if (searchParams.has('loc') && hasCoordinates) {
    criteria.loc = searchParams.get('loc');
    criteria.long = long;
    criteria.lat = lat;
    if (searchParams.has('radius')) {
      const rawRadius = searchParams.get('radius');
      if (rawRadius === '') {
        criteria.radius = null;
      } else {
        const radius = Number.parseInt(rawRadius, 10);
        if (!Number.isNaN(radius)) criteria.radius = radius;
      }
    }
  }
  if (searchParams.has('pmin')) criteria.pmin = searchParams.get('pmin').replace(/\D/g, '');
  if (searchParams.has('pmax')) criteria.pmax = searchParams.get('pmax').replace(/\D/g, '');

  Object.entries(RANGE_PARAMS).forEach(([key, [minParam, maxParam]]) => {
    const min = Number.parseInt(searchParams.get(minParam), 10);
    const max = Number.parseInt(searchParams.get(maxParam), 10);
    if (Number.isNaN(min) && Number.isNaN(max)) return;
    criteria[key] = [Number.isNaN(min) ? null : min, Number.isNaN(max) ? null : max];
  });

  CSV_PARAMS.forEach((key) => {
    const value = searchParams.get(key);
    if (value) criteria[key] = value.split(',').filter(Boolean);
  });

  // Folded onto the offered buttons, the way the statuses are filtered below: a shared link or a stored search from before WHN-2148 can
  // still say `beds=4`, and without this it would filter on exactly four
  // bedrooms while no button shows it.
  if (searchParams.has('beds')) {
    criteria.beds = [
      ...new Set(
        criteria.beds
          .map((bed) => Number.parseInt(bed, 10))
          .filter((bed) => !Number.isNaN(bed) && bed >= BEDROOM_OPTIONS[0])
          .map((bed) => Math.min(bed, BEDROOM_PLUS_FROM))
      )
    ];
  }

  // Filtered against the offered filters, the way the statuses are below. A link
  // from before a rename carries a filter name that no longer exists
  // ('types=Rijwoning', WHN-2147): dropping it widens the results rather than
  // showing a pill for a filter that is not there.
  criteria.types = criteria.types.filter((slug) => HOUSING_TYPE_SLUGS.includes(slug));

  const validStatuses = STATUS_OPTIONS.map((option) => option.slug);
  criteria.statuses = criteria.statuses.filter((slug) => validStatuses.includes(slug));

  const sort = searchParams.get('sort');
  if (sort && SORT_OPTIONS.some((option) => option.slug === sort)) criteria.sort = sort;

  return criteria;
};

export const hasCriteriaParams = (searchParams) =>
  CRITERIA_PARAMS.some((param) => searchParams.has(param));

// Marketing/tracking params (Google Analytics, Ads, UTM) are not filter
// criteria, but a criteria change rebuilds the querystring from scratch and
// would otherwise silently drop them. Whitelisted so they survive every
// history.replaceState/navigation alongside the canonical criteria params.
const TRACKING_PARAMS = [
  '_gl',
  'gclid',
  'gclsrc',
  'dclid',
  'fbclid',
  'msclkid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content'
];

export const withTrackingParams = (params, sourceSearchParams) => {
  TRACKING_PARAMS.forEach((param) => {
    if (sourceSearchParams.has(param)) params.set(param, sourceSearchParams.get(param));
  });
  return params;
};
